import {
  Tab,
  Tabs,
  Typography,
  type SxProps,
  type Theme,
  type TypographyProps,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { createContext, useContext } from "react"
import { extraColors } from "../../utils/theme"
import { TOOLBAR_HEIGHT } from "../DrawerNav"

export type DrawerVariant = "default" | "command"

/**
 * Lets a drawer restyle its shared primitives (section labels, chips, toggles,
 * paper surface) for the v2 "command console" without threading a prop through
 * every child. The v2 views render their drawer with variant="command".
 */
const DrawerVariantContext = createContext<DrawerVariant>("default")
export const DrawerVariantProvider = DrawerVariantContext.Provider
export const useDrawerVariant = (): DrawerVariant =>
  useContext(DrawerVariantContext)

const MONO = 'ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace'
const COMMAND_ACCENT = "rgba(55, 178, 255, 0.9)"

const sectionLabelDefaultSx = {
  fontWeight: 800,
  color: extraColors.translucent50,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontSize: "0.65rem",
  marginBottom: 8,
} as const

const sectionLabelCommandSx = {
  fontFamily: MONO,
  fontWeight: 700,
  color: COMMAND_ACCENT,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.14em",
  fontSize: "0.75rem",
  marginBottom: 8,
} as const

const composeSx = (
  base: SxProps<Theme>,
  override: SxProps<Theme> | undefined
): SxProps<Theme> => {
  const overrides: SxProps<Theme>[] = Array.isArray(override)
    ? (override as SxProps<Theme>[])
    : override
      ? [override]
      : []
  return [base, ...overrides] as SxProps<Theme>
}

export const DrawerSectionLabel: React.FC<TypographyProps> = ({
  sx,
  ...props
}) => {
  const variant = useDrawerVariant()
  const base =
    variant === "command" ? sectionLabelCommandSx : sectionLabelDefaultSx
  return <Typography {...props} sx={composeSx(base, sx)} />
}

/** Centered chart / section headings (0.8rem — matches site summary title). */
export const DrawerChartTitle: React.FC<TypographyProps> = ({
  sx,
  ...props
}) => {
  const variant = useDrawerVariant()
  const base: SxProps<Theme> =
    variant === "command"
      ? { ...sectionLabelCommandSx, textAlign: "center" }
      : { ...sectionLabelDefaultSx, fontSize: "0.8rem", textAlign: "center" }
  return <Typography {...props} sx={composeSx(base, sx)} />
}

export const CompactTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  minHeight: 34,
  borderBottom: `1px solid ${extraColors.translucent5}`,
}))

export const CompactTab = styled(Tab)({
  minHeight: 34,
  paddingTop: 4,
  paddingBottom: 4,
  textTransform: "none",
  fontSize: "0.8rem",
})

export const summaryChipSx = {
  borderColor: extraColors.translucentWhite,
  backgroundColor: "rgba(255, 255, 255, 0.08)",
  "& .MuiChip-label": { color: extraColors.white, fontWeight: 600 },
} as const

const precomputedToggleButtonSx = {
  color: extraColors.translucent50,
  borderColor: extraColors.translucentWhite,
  textTransform: "none",
  "&.Mui-selected": {
    color: extraColors.white,
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
  },
} as const

export const precomputedToggleGroupSx = {
  "& .MuiToggleButton-root": {
    ...precomputedToggleButtonSx,
    fontSize: "0.65rem",
    py: 0.25,
    px: 1,
    lineHeight: 1.4,
  },
} as const

/** Historic / Event scope control in the NWSS drawer. */
export const nwssScopeToggleGroupSx = {
  "& .MuiToggleButton-root": {
    ...precomputedToggleButtonSx,
    fontSize: "0.8rem",
    py: 0.625,
    px: 1.75,
    lineHeight: 1.3,
    minHeight: 36,
    minWidth: 80,
  },
} as const

/**
 * Command-console surface shared by the drawer paper and the standalone detail
 * page: dark background, corner brackets, and restyled chips/toggles. The
 * corner-bracket pseudo-elements are absolutely positioned, so any consumer
 * that is not already positioned (e.g. the detail page container) must set
 * `position: "relative"` itself.
 */
export const commandSurfaceSx = {
  bgcolor: "#0B0F14",
  backgroundImage: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 8,
    left: 8,
    width: 16,
    height: 16,
    borderTop: `2px solid ${COMMAND_ACCENT}`,
    borderLeft: `2px solid ${COMMAND_ACCENT}`,
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 8,
    right: 8,
    width: 16,
    height: 16,
    borderBottom: `2px solid ${COMMAND_ACCENT}`,
    borderRight: `2px solid ${COMMAND_ACCENT}`,
    pointerEvents: "none",
  },
  "& .MuiChip-root": {
    borderRadius: "13px",
    border: "1px solid rgba(255, 255, 255, 0.13)",
    backgroundColor: "transparent",
    fontFamily: MONO,
    "& .MuiChip-label": {
      color: "#D5DAE0",
      fontWeight: 600,
      letterSpacing: "0.03em",
    },
  },
  "& .MuiToggleButton-root": {
    fontFamily: MONO,
    letterSpacing: "0.04em",
  },
} as const

/** Drawer-paper command treatment: the shared surface plus the left edge. */
const commandPaperExtras = {
  ...commandSurfaceSx,
  borderLeft: "1px solid rgba(55, 178, 255, 0.25)",
  boxShadow: "-24px 0 60px rgba(0, 0, 0, 0.5)",
} as const

export const precomputedDrawerPaperSx = (
  width: number,
  variant: DrawerVariant = "default"
) => ({
  top: TOOLBAR_HEIGHT,
  height: `calc(100% - ${TOOLBAR_HEIGHT}px)`,
  width,
  bgcolor: extraColors.dark,
  borderLeft: `1px solid ${extraColors.translucentWhite}`,
  p: 3,
  display: "flex",
  flexDirection: "column",
  ...(variant === "command" ? commandPaperExtras : {}),
})
